import {
    Component,
    EventEmitter,
    Input,
    OnInit,
    Output,
    ViewChild,
    ChangeDetectorRef,
} from '@angular/core';
import { combineLatest, Observable, Subject, of } from 'rxjs';
import {
    catchError,
    filter,
    take,
    tap,
    takeUntil,
    debounceTime,
    distinctUntilChanged,
    switchMap,
} from 'rxjs/operators';
import { FormGroup } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { User, UserGroup, UserService } from '@intranet/modules/users';

import {
    JobActionSearchQuery,
    ActionService,
    JobActionTimePeriod,
    JobActionType,
} from '@intranet/modules/actions';
import { Client } from '@intranet/modules/clients';
import { ClientsService } from '@intranet/modules/clients/clients.service';
import { orderClientsByGroupName } from '@intranet/modules/clients/client.helpers';
import { LoginService } from '@intranet/modules/login/login.service';
import { JobService } from '@intranet/modules/jobs/job.service';
import { JobType } from '@intranet/modules/jobs/job.types';
import { NO_JOB_TYPES } from '@intranet/components/job-type-picker';
import { AlertDialog } from '@intranet/shared/alert/alert-dialog.component';

@Component({
    selector: 'action-search',
    templateUrl: './action-search.component.html',
    styleUrls: ['./action-search.component.scss'],
})
export class ActionSearchComponent implements OnInit {
    private _unsubscribeAll: Subject<any> = new Subject<any>();
    @ViewChild('form', { static: true }) form: FormGroup;
    @Output() filter = new EventEmitter<JobActionSearchQuery>();
    @Input() owner: string = '';
    @Input() clientId?: number;
    @Input() jobId: number;
    @Input() alwaysShowClient: boolean = false;
    @Input() timePeriod: JobActionTimePeriod = JobActionTimePeriod.Today;
    @Input() type?: JobActionType;
    @Input() jobTypeIds?: number[];

    filterClients: [Client[], Client[]] = [[], []];
    firstLoad: boolean = true;
    filterQuery: JobActionSearchQuery;
    activeUsers: User[] = [];
    otherUsers: User[] = [];
    groups: UserGroup[] = [];
    jobTypes: JobType[] = [];

    // Loading state for the owner filter
    isLoadingOwners: boolean = true;

    // Phones: the filters other than Search are folded away behind a
    // "Filters" button (see the template and stylesheet) — open or not.
    filtersOpen = false;

    // PM-role gate + in-flight state for the "Create Snapshot" button
    isPM: boolean = false;
    isCreatingSnapshot: boolean = false;

    // The currently selected user/group for display (may not be in dropdown options)
    selectedOwner: User | null = null;
    selectedGroupName: string | null = null;

    // Store all users and groups for filtering
    private _allUsers: User[] = [];
    private _allGroups: UserGroup[] = [];

    // Subject to trigger re-fetching of action owners when client changes
    private _clientChanged$ = new Subject<number | undefined>();

    // Cache for "all owners" (no client filter) to avoid re-fetching
    private _allOwnersCache: { userIds: string[]; groupIds: number[] } | null = null;

    constructor(
        private _clientService: ClientsService,
        private _userService: UserService,
        private _actionService: ActionService,
        private _jobService: JobService,
        private _loginService: LoginService,
        private _cdr: ChangeDetectorRef,
        private _dialog: MatDialog,
        private _router: Router
    ) {}

    ngOnInit(): void {
        this.isPM = this._loginService.checkAuth(['PM']);

        let observables$: Observable<any>[] = [];
        observables$.push(this._userService.users$);
        observables$.push(this._userService.groups$);
        observables$.push(this._actionService.filterQuery$);
        observables$.push(this._clientService.clients$);
        observables$.push(this._clientService.myClients$);
        observables$.push(this._jobService.jobTypes$);

        combineLatest(observables$)
            .pipe(
                // The users/teams lists start out empty (null) until the
                // app's users request returns. Taking that first, empty value
                // left _allUsers null, so filtering the owners threw, and the
                // Owner picker stayed on "Loading..." for good (and the
                // first filter never went out) whenever this page was quicker
                // than that request. Wait for them to arrive instead.
                filter(([users, groups]) => !!users && !!groups),
                take(1),
                switchMap(([users, groups, filterQuery, allClients, myClients, jobTypes]) => {
                    // Store all users and groups
                    this._allUsers = users;
                    this._allGroups = groups;
                    this.jobTypes = jobTypes;

                    // Set initial query values (owner will be set after we fetch action owners)
                    this.filterQuery = {
                        ...filterQuery,
                        timePeriod: this.timePeriod || JobActionTimePeriod.Today,
                        type: this.type || '',
                        jobTypeIds: this.jobTypeIds || [],
                        owner: this.owner,
                        clientId: this.clientId || '',
                        jobId: this.jobId,
                        status: null,
                    };
                    filterQuery.clientId = this.filterQuery.clientId;
                    filterQuery.type = this.filterQuery.type;

                    // Clients
                    if (allClients && myClients) {
                        const [pinnedClients, restClients] =
                            orderClientsByGroupName(
                                allClients,
                                myClients.map((c) => c.clientId),
                                true
                            );
                        this.filterClients = [
                            pinnedClients as Client[],
                            restClients as Client[],
                        ];
                    }

                    // Fetch action owners for initial load (considering initial clientId)
                    const initialClientId = this.clientId ? Number(this.clientId) : undefined;
                    return this._actionService.getActionOwners(initialClientId).pipe(
                        // If that request fails, offer everyone rather than
                        // leaving the picker on "Loading..." for good.
                        catchError(() => of({
                            userIds: (users as User[]).map((u) => u.id),
                            groupIds: (groups as UserGroup[]).map((g) => g.userGroupId),
                        }))
                    );
                })
            )
            .subscribe((owners) => {
                // Cache all owners on initial load (when no client filter)
                const isMainActionsPage = !this.clientId;
                if (isMainActionsPage) {
                    this._allOwnersCache = owners;
                }

                // Filter users and groups based on action owners
                this._filterUsersAndGroups(owners.userIds, owners.groupIds);

                // Pre-select current user if they have actions and no owner was explicitly set
                if (!this.owner) {
                    const currentUserId = this._loginService.userId;
                    const hasActions = owners.userIds.includes(currentUserId);
                    if (hasActions) {
                        this.filterQuery.owner = currentUserId;
                    }
                }

                // Mark loading as complete
                this.isLoadingOwners = false;
                this._cdr.detectChanges();

                // Fire on first load (after owners are loaded)
                this.filter.emit(this.filterQuery);
            });

        this._actionService.filterQuery$
            .pipe(
                takeUntil(this._unsubscribeAll),
                tap((filterQuery) => {
                    this.filterQuery = { ...filterQuery };
                })
            )
            .subscribe();

        // Watch for client changes and re-fetch action owners
        this._clientChanged$
            .pipe(
                takeUntil(this._unsubscribeAll),
                switchMap((clientId) => {
                    // Use cache when switching to "All" (no client filter)
                    if (!clientId && this._allOwnersCache) {
                        return of(this._allOwnersCache);
                    }
                    return this._actionService.getActionOwners(clientId);
                })
            )
            .subscribe((owners) => {
                this._filterUsersAndGroups(owners.userIds, owners.groupIds);
                this.isLoadingOwners = false;
            });

        this.form.valueChanges
            .pipe(debounceTime(400), distinctUntilChanged())
            .subscribe((values) => {
                if (!this.form.pristine) {
                    this.filter.emit({
                        ...this.filterQuery,
                        ...values,
                        page: 0,
                    });
                }
            });
    }

    /**
     * Filters users and groups based on the provided owner IDs.
     */
    private _filterUsersAndGroups(userIds: string[], groupIds: number[]): void {
        const userIdSet = new Set(userIds);
        const groupIdSet = new Set(groupIds);

        // Filter users to only those who own actions
        const filteredUsers = this._allUsers.filter((u) => userIdSet.has(u.id));
        this.activeUsers = filteredUsers.filter((u) => u.active);
        this.otherUsers = filteredUsers.filter((u) => !u.active);

        // Filter groups to only those who own actions
        this.groups = this._allGroups.filter((g) => groupIdSet.has(g.userGroupId));

        // Update selected owner for display purposes
        this._updateSelectedOwnerDisplay();
    }

    // ─── Owner filter: multi-select ───────────────────────────────────────
    // The picker works with a list of ids (users, and teams as
    // "group-<id>"); the query keeps the single owner string the rest of
    // the page, the server and the Planner already understand:
    //   none → '' (All) · one → that id / "group-<id>", exactly as before ·
    //   several → "many|id|id|group-<id>…" (the format the Home page already
    //   uses, and the server's search already accepts).

    private _ownerSelectionMemo: { owner: string; ids: string[] } = { owner: '', ids: [] };

    /** The current owner as a list for the picker. Memoised so the same
     *  array goes back each time — a fresh one on every change detection
     *  would make [ngModel] re-write the picker endlessly. */
    get ownerSelection(): string[] {
        const owner = this.filterQuery?.owner || '';
        if (owner !== this._ownerSelectionMemo.owner) {
            const ids = !owner ? []
                : owner.startsWith('many|') ? owner.replace('many|', '').split('|').filter((id) => !!id)
                : [owner];
            this._ownerSelectionMemo = { owner, ids };
        }
        return this._ownerSelectionMemo.ids;
    }

    private toOwnerQuery(ids: string[]): string {
        if (!ids || ids.length === 0) return '';
        if (ids.length === 1) return ids[0];
        return 'many|' + ids.join('|');
    }

    // "Apply" in the picker (or its ✕ / "All", which apply an empty list).
    onOwnersApplied(ids: string[]): void {
        this.filterQuery.owner = this.toOwnerQuery(ids);
        this._updateSelectedOwnerDisplay();
        this.filter.emit(this.filterQuery);
    }

    // Updates the selectedOwner and selectedGroupName based on the current filterQuery
    private _updateSelectedOwnerDisplay(): void {
        const currentOwner = this.filterQuery?.owner;

        // Nothing, or several owners (the picker labels those itself).
        if (!currentOwner || currentOwner.startsWith('many|')) {
            this.selectedOwner = null;
            this.selectedGroupName = null;
            return;
        }

        if (currentOwner.startsWith('group-')) {
            const groupId = parseInt(currentOwner.replace('group-', ''), 10);
            const group = this._allGroups.find((g) => g.userGroupId === groupId);
            this.selectedOwner = null;
            this.selectedGroupName = group?.groupName || null;
        } else {
            const user = this._allUsers.find((u) => u.id === currentOwner);
            this.selectedOwner = user || null;
            this.selectedGroupName = null;
        }
    }

    private static readonly WHEN_LABELS: Record<string, string> = {
        today: 'Today',
        todaysdeadlines: "Today's deadlines",
        tomorrow: 'Tomorrow',
        todaytomorrow: 'Today & Tomorrow',
        week: 'This week',
        nextweek: 'Next week',
        all: 'All dates',
    };

    private static readonly TYPE_LABELS: Record<string, string> = {
        '10': 'Standard',
        '50': 'Approval',
        '100': 'Invoice',
    };

    /** Phones, with the filters folded away: what they're set to, in a
     *  line — e.g. "Me · Today" or "2 owners · This week · Invoice". */
    get filtersSummary(): string {
        const q = this.filterQuery;
        if (!q) return '';
        const parts: string[] = [];

        if (q.clientId) {
            const client = [...this.filterClients[0], ...this.filterClients[1]]
                .find((c) => String(c.clientId) === String(q.clientId));
            if (client) parts.push(client.displayName || client.clientName);
        }

        const owner = q.owner || '';
        if (!owner) {
            parts.push('All owners');
        } else if (owner.startsWith('many|')) {
            parts.push(`${this.ownerSelection.length} owners`);
        } else if (owner === this._loginService.userId) {
            parts.push('Me');
        } else if (owner.startsWith('group-')) {
            parts.push(this.selectedGroupName || '1 team');
        } else {
            const user = this.selectedOwner;
            parts.push(user ? user.fullName || `${user.firstName} ${user.surname}` : '1 owner');
        }

        parts.push(ActionSearchComponent.WHEN_LABELS[q.timePeriod || 'today'] || 'Today');

        const jobTypeIds = q.jobTypeIds || [];
        if (jobTypeIds.length === 1 && jobTypeIds[0] === NO_JOB_TYPES) {
            parts.push('No job types');
        } else if (jobTypeIds.length === 1) {
            parts.push(this.jobTypes.find((t) => t.jobTypeId === jobTypeIds[0])?.description || '1 job type');
        } else if (jobTypeIds.length > 1) {
            parts.push(`${jobTypeIds.length} job types`);
        }

        const type = ActionSearchComponent.TYPE_LABELS[String(q.type ?? '')];
        if (type) parts.push(type);

        return parts.join(' \u00b7 ');
    }

    /**
     * Called when the client filter changes to update available owners.
     */
    onClientChange(clientId: number | string | undefined): void {
        const numericClientId = clientId ? Number(clientId) : undefined;
        this._clientChanged$.next(numericClientId || undefined);
    }

    // Only shown to PM-role users while "Today's deadlines" is selected (see
    // the *ngIf in the template). Persists the actions currently matching every
    // active filter into the new snapshot tables - if no owner is selected, the
    // resulting ActionSnapshotGroup has no ActionSnapshotGroupUsers, which is
    // interpreted as "All Users" when the snapshot is displayed later.
    onCreateSnapshot() {
        if (this.isCreatingSnapshot) {
            return;
        }
        this.isCreatingSnapshot = true;
        this._actionService.createSnapshot(this.filterQuery).subscribe({
            next: (result) => {
                this.isCreatingSnapshot = false;
                this._cdr.detectChanges();
                // "View Snapshot" navigates within the same window/tab
                // (plain router navigation) rather than opening a new one.
                this._dialog.open(AlertDialog, {
                    width: '400px',
                    data: {
                        title: 'Snapshot Captured',
                        message:
                            'A snapshot of the current actions has been captured.',
                        confirm: false,
                        confirmTitle: '',
                        actionTitle: 'View Snapshot',
                        action: () =>
                            this._router.navigate([
                                '/report/actionsnapshot',
                                result.actionSnapshotGroupId,
                            ]),
                    },
                });
            },
            error: (e) => {
                this.isCreatingSnapshot = false;
                this._cdr.detectChanges();
                console.log(e);
                this._dialog.open(AlertDialog, {
                    width: '320px',
                    data: {
                        title: 'Snapshot Failed',
                        message:
                            e?.message ||
                            'Something went wrong capturing the snapshot.',
                        confirm: false,
                        confirmTitle: '',
                    },
                });
            },
        });
    }

    onClearFilters() {
        // The Owner picker isn't one of the form's controls any more (it's
        // multi-select, kept apart from the form — see the template), so
        // it's cleared here directly, before the form change below emits.
        this.filterQuery.owner = '';
        this._updateSelectedOwnerDisplay();
        this.form.setValue({
            clientId: '',
            searchTerm: '',
            timePeriod: JobActionTimePeriod.Today,
            type: '',
            jobTypeIds: [],
        });
        // Reset to show all action owners when clearing filters
        this._clientChanged$.next(undefined);
    }

    ngOnDestroy() {
        this._unsubscribeAll.next('');
        this._unsubscribeAll.complete();
    }
}
